import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Text,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useBrand} from '../../contexts/brand-context'
import {buildUgcItemPath} from '../../utils/url'
import {getUgcTileCreatorDisplayLabel} from '../../utils/ugc-creator'
import {useInViewport} from '../../hooks/use-in-viewport'
import UgcVideoPlayer from '../ugc-video-player'
import {sendEvent, INTERACTION_LOCATION} from '../../utils/gtm-utils'

const MAIN_IN_VIEW_RATIO = 0.6

/**
 * Single tile in the UGC grid. Desktop: hover plays, unhover pauses BEFORE
 * the user clicks the play/pause button — after a click, the video player
 * itself takes full permanent control and this hover state has no further
 * effect. Mobile/touch has no hover gesture, so by default the video stays
 * PAUSED until the user taps the player's play button. Pass `autoplayOnMobile`
 * (e.g. the Home Page carousel) to auto-play muted video on mobile — but only
 * for the tile currently in the main viewport, never the peeking neighbour.
 * The player's play/pause button still lets the user stop it.
 */
const UgcTile = ({item, isLargeGridView, cgid, autoplayOnMobile = false, analyticsPlacement = 'lp'}) => {
    const styles = useMultiStyleConfig('UgcTile', {isLargeGridView})
    const intl = useIntl()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [isHovered, setIsHovered] = useState(false)
    const {brandId} = useBrand()
    const {observeRef, intersectionRatio} = useInViewport({
        rootMargin: '0px',
        threshold: [0, 0.25, MAIN_IN_VIEW_RATIO, 0.75, 1]
    })
    const isPrimaryInView = intersectionRatio >= MAIN_IN_VIEW_RATIO

    if (!item) return null

    // BV/Curalate media shape: item.media = { type, highQuality:{link},
    // original:{link}, medium:{link}, lowQuality:{link}, poster:{...same
    // rendition keys...} }. Resolve the best available URL for an image or
    // video from the rendition object.
    const renditionLink = (media, keys) => {
        for (const key of keys) {
            const variant = media?.[key]
            if (typeof variant === 'string') return variant
            if (variant?.link) return variant.link
        }
        return ''
    }
    const media = item.media || {}
    const isVideo = media.type === 'video'
    // BV/Curalate Vibe rendition keys, confirmed from the working SFRA
    // ugcCarouselSlot.isml: highQuality, original, medium, lowQuality.
    // Video renditions live at the top level of `media` itself (no nested
    // `media.video`), and the poster image lives under `media.poster` using
    // the same rendition key set.
    const imageSrc = renditionLink(media, ['highQuality', 'original', 'medium', 'lowQuality'])
    const videoSrc = renditionLink(media, ['highQuality', 'original', 'lowQuality'])
    const posterSrc = renditionLink(media.poster, [
        'highQuality',
        'original',
        'medium',
        'lowQuality'
    ])

    // Handle-first: "@username" when the creator has one (so shoppers can
    // search/follow), else the display name, else the brand handle — with no
    // "@" prefix. The payload's brand field is not used. See
    // getUgcTileCreatorDisplayLabel — item.creatorHandle IS the source.user.
    const creatorLabel =
        getUgcTileCreatorDisplayLabel(item, brandId) ||
        intl.formatMessage({
            id: 'ugc_tile.assistive_msg.default_creator',
            defaultMessage: 'UGC media tile'
        })

    // Desktop: hover drives play/pause. Mobile: autoplay only while this tile is
    // the MAIN one on screen — a peeking neighbour stays paused.
    const shouldTrackHover = isDesktop ? isHovered : autoplayOnMobile && isPrimaryInView

    return (
        <Box
            {...styles.container}
            ref={observeRef}
            data-testid="ugc-tile"
            onMouseEnter={() => isDesktop && setIsHovered(true)}
            onMouseLeave={() => isDesktop && setIsHovered(false)}
        >
            <Box {...styles.mediaContainer}>
                {isVideo ? (
                    <UgcVideoPlayer
                        src={videoSrc}
                        poster={posterSrc}
                        isHovered={shouldTrackHover}
                    />
                ) : (
                    <Box
                        as="img"
                        src={imageSrc}
                        alt={creatorLabel}
                        loading="lazy"
                        {...styles.image}
                    />
                )}
            </Box>
            {!isLargeGridView && <Text {...styles.caption}>{creatorLabel}</Text>}
            {/* Overlay link; video controls stay outside the anchor. */}
            <Link
                to={buildUgcItemPath(brandId, item.id, cgid)}
                onClick={() => sendEvent('custom_click', {
                    type: analyticsPlacement === 'hp' ? 'carousel - hp - ugc' : 'tile - lp - ugc',
                    interaction_location: analyticsPlacement === 'hp'
                        ? INTERACTION_LOCATION.UGC_CAROUSEL
                        : INTERACTION_LOCATION.UGC_TILE
                })}
                {...styles.link}
                aria-label={intl.formatMessage(
                    {
                        id: 'ugc_tile.assistive_msg.view_look',
                        defaultMessage: 'View look by {creator}'
                    },
                    {creator: creatorLabel}
                )}
            />
        </Box>
    )
}

UgcTile.propTypes = {
    analyticsPlacement: PropTypes.oneOf(['hp', 'lp']),
    item: PropTypes.shape({
        id: PropTypes.string,
        media: PropTypes.shape({
            type: PropTypes.oneOf(['photo', 'video']),
            highQuality: PropTypes.shape({link: PropTypes.string}),
            original: PropTypes.shape({link: PropTypes.string}),
            medium: PropTypes.shape({link: PropTypes.string}),
            lowQuality: PropTypes.shape({link: PropTypes.string}),
            poster: PropTypes.shape({
                highQuality: PropTypes.shape({link: PropTypes.string}),
                original: PropTypes.shape({link: PropTypes.string}),
                medium: PropTypes.shape({link: PropTypes.string}),
                lowQuality: PropTypes.shape({link: PropTypes.string})
            })
        }),
        creatorHandle: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                username: PropTypes.string,
                displayName: PropTypes.string,
                handle: PropTypes.string,
                link: PropTypes.string,
                image: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.shape({
                        original: PropTypes.shape({link: PropTypes.string})
                    })
                ]),
                followerCount: PropTypes.number,
                followingCount: PropTypes.number
            })
        ]),
        creator: PropTypes.shape({
            username: PropTypes.string,
            displayName: PropTypes.string,
            handle: PropTypes.string,
            link: PropTypes.string,
            image: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    original: PropTypes.shape({link: PropTypes.string})
                })
            ]),
            followerCount: PropTypes.number,
            followingCount: PropTypes.number
        }),
        detailUrl: PropTypes.string,
        hasProducts: PropTypes.bool
    }),
    isLargeGridView: PropTypes.bool,
    cgid: PropTypes.string,
    autoplayOnMobile: PropTypes.bool
}

export default UgcTile
